import { ChatHistoryItem, FileConfig, UploadResponse, UseScrollContainerProps } from "./types/common";

import { useEffect } from "react";
import { showErrorToast } from "./toastUtils";
import { ALLOWED_MODEL_FILE_TYPES, MAX_MODEL_FILE_SIZE } from "./Constants/Constants";
import CryptoJS from "crypto-js";

export const validateName = (name: string): boolean => {
    const regex = /^[a-zA-Z0-9 ]+$/; // Allow letters, numbers and spaces
    return regex.test(name) && name?.length > 3;
};

export const containsHTML = (str: string): boolean => {
    const htmlRegex = /<([A-Za-z][A-Za-z0-9]*)\b[^>]*>(.*?)<\/\1>|<([A-Za-z][A-Za-z0-9]*)\b[^>]*\/>/;
    return htmlRegex.test(str);
};

export const handleWorkbenchNameInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    // Check if first character is a letter
    if (value.length > 0 && !/^[a-zA-Z]/.test(value)) {
        event.target.value = value.slice(1);
        showErrorToast("Workbench name must start with a letter.");
        return;
    }

    // Optional: Prevent multiple consecutive spaces
    if (/\s\s/.test(value)) {
        event.target.value = value.replace(/\s+/g, ' ');
        showErrorToast("Multiple consecutive spaces are not allowed.");
    }
};

export const credentialEncryption = (text: string) => {
    const encryptionKey = '*poj.ocjliljoewapkmcyju-taoilyi76+l646'
    const key = process.env.REACT_APP_DB_PASS_SECRET_KEY as string || encryptionKey;
    let encryptdetails = CryptoJS.AES.encrypt(text, key).toString();
    return encryptdetails;
}

export const credentialDecryption = (cipherText: string): string => {
    const encryptionKey = '*poj.ocjliljoewapkmcyju-taoilyi76+l646'
    const key = process.env.REACT_APP_DB_PASS_SECRET_KEY as string || encryptionKey;
    const bytes = CryptoJS.AES.decrypt(cipherText, key);
    const decryptedText = bytes.toString(CryptoJS.enc.Utf8);
    return decryptedText;
}

export const allowOnlyAlphaNumericAndSpaces = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const regex = /^[a-zA-Z0-9\s]*$/; // Only allows letters, numbers, and spaces
    if (!regex.test(event.key)) {
        event.preventDefault();
        showErrorToast("Only letters, numbers, and spaces are allowed.");
    }
};

export const downloadFileFromUrl = (url: string, customFilename?: string) => {
    const link = document.createElement('a');
    link.href = url;

    if (customFilename) {
        link.setAttribute('download', customFilename);
    } else {
        const urlParts = url.split('/');
        const filenameWithParams = urlParts[urlParts.length - 1];
        const filename = filenameWithParams.split('?')[0];

        link.setAttribute('download', filename);
    }

    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// Get the position of the highlight grid view
export const getHighlightPosition = (hoveredView: any, activeView: any) => {
    const position = hoveredView || activeView;
    const buttonWidth = (document.querySelector('.list-view-button') as HTMLElement)?.offsetWidth || 0;
    switch (position) {
        case 'list':
            return 0;
        case 'full':
            return buttonWidth;
        case 'smallGrid':
            return buttonWidth * 2;
        case 'largeGrid':
            return buttonWidth * 3;
        default:
            return 0;
    }
};

// Get the position of the filter highlight
export const getFilterHighlightPosition = (hoveredFilter: any, activeFilter: any) => {
    const position = hoveredFilter || activeFilter;
    // Calculate highlight position dynamically
    const buttonWidth = (document.querySelector('.all-models-button') as HTMLElement)?.offsetWidth || 0;
    switch (position) {
        case 'all':
            return 0;
        case 'created':
            return buttonWidth;
        case 'team':
            return buttonWidth * 2;
        case 'shared':
            return buttonWidth * 3;
        default:
            return 0;
    }
};

// Get the actions highlight position
export const getActionsHighlightPosition = (actionsHovered: any, actionsActive: any) => {
    const position = actionsHovered || actionsActive;
    const buttonWidth = (document.querySelector('.merge-button') as HTMLElement)?.offsetWidth || 0;
    switch (position) {
        case 'merge':
            return 0;
        case 'delete':
            return buttonWidth;
        default:
            return 0;
    }
};

// Get current state of buttons based on selected model and selected keys
export const getButtonState = (list: any, selectModel: boolean, selectedKeys: string[], type: string) => {

    const getBaseState = () => ({
        opacity: 0.5,
        cursor: 'not-allowed',
        isEnabled: false
    });

    if (!list || list.length === 0) {
        return getBaseState();
    }

    switch (type) {
        case 'check':
            return {
                opacity: 1,
                cursor: 'pointer',
                isEnabled: true
            };
        case 'delete':
            return {
                opacity: selectModel && selectedKeys.length > 0 ? 1 : 0.5,
                cursor: selectModel && selectedKeys.length > 0 ? 'pointer' : 'not-allowed',
                isEnabled: selectModel && selectedKeys.length > 0
            };
        case 'merge':
            return {
                opacity: selectModel && selectedKeys.length > 1 ? 1 : 0.5,
                cursor: selectModel && selectedKeys.length > 1 ? 'pointer' : 'not-allowed',
                isEnabled: selectModel && selectedKeys.length > 1
            };
        default:
            return getBaseState();
    }
};

//Handle File Upload
const validateFile = (file: File, config: FileConfig): string | null => {
    if (config.acceptedTypes && config.acceptedTypes.length > 0) {
        if (!config.acceptedTypes.includes(file.type)) {
            return `File type must be: ${config.acceptedTypes.join(', ')}`;
        }
    }

    if (config.minSizeInKB && file.size < config.minSizeInKB * 1024) {
        return `File must be at least ${config.minSizeInKB}KB`;
    }

    if (config.maxSizeInMB && file.size > config.maxSizeInMB * 1024 * 1024) {
        return `File must not exceed ${config.maxSizeInMB}MB`;
    }

    return null;
};

export const uploadFile = async (
    file: File,
    uploadMutation: any,
    config: FileConfig = {},
    onSuccess?: (data: any) => void,
    onError?: (error: string) => void
): Promise<UploadResponse> => {
    try {
        const validationError = validateFile(file, config);
        if (validationError) {
            showErrorToast(validationError);
            onError?.(validationError);
            return { success: false, error: validationError };
        }

        // Create a new FormData instance
        const formData = new FormData();

        // const timestamp = new Date().getTime();
        const modifiedFile = new File([file], `${file.name}`, {
            type: file.type,
            lastModified: new Date().getTime(),
        });

        formData.append('file', modifiedFile);

        const response = await uploadMutation(formData).unwrap();

        if (response?.status_code === 200) {
            onSuccess?.(response?.data);
            return { success: true, data: response?.data };
        } else {
            throw new Error(response?.message || 'Upload failed');
        }
    } catch (error: any) {
        const errorMessage = error?.data?.message || error?.message || 'Upload failed';
        showErrorToast(errorMessage);
        onError?.(errorMessage);
        return { success: false, error: errorMessage };
    }
};

export const useScrollContainer = ({ ref, enabled = true }: UseScrollContainerProps) => {
    useEffect(() => {
        const element = ref.current;

        const handleScroll = (event: WheelEvent) => {
            if (!element || !enabled) return;

            const { scrollTop, scrollHeight, clientHeight } = element;
            const isScrollingUp = event.deltaY < 0;
            const isScrollingDown = event.deltaY > 0;

            // Check if we've reached the top or bottom of the inner scroll
            const isAtTop = scrollTop === 0;
            const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1;

            // Only prevent default if we're not at the boundaries
            // or if we're at a boundary but trying to scroll further in that direction
            if ((!isAtTop || !isScrollingUp) && (!isAtBottom || !isScrollingDown)) {
                event.stopPropagation();
            }
        };

        if (element) {
            element.addEventListener('wheel', handleScroll);
            return () => element.removeEventListener('wheel', handleScroll);
        }
    }, [ref, enabled]);
};

export const validateModelFile = (file: File, showToast: boolean = true): boolean => {
    if (!ALLOWED_MODEL_FILE_TYPES.includes(file.type)) {
        if (showToast) {
            showErrorToast('Invalid file type. Please upload a CSV, XLS, or XLSX file.');
        }
        return false;
    }

    if (file.size > MAX_MODEL_FILE_SIZE) {
        if (showToast) {
            showErrorToast('File size exceeds 5MB limit. Please upload a smaller file.');
        }
        return false;
    }

    return true;
};

export const formatFileSize = (size: number): string => {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

//Transform Chat History Data
export const transformChatHistoryData = (apiData: any[]): ChatHistoryItem[] => {
    if (!apiData || !Array.isArray(apiData)) {
        return [];
    }

    return apiData.map((item: any, index: number) => {
        const answersCopy = item.answers && Array.isArray(item.answers) ? [...item.answers] : [];
        const sortedAnswers = answersCopy.length > 0
            ? answersCopy.sort(
                (a: any, b: any) =>
                    new Date(b.created_on).getTime() - new Date(a.created_on).getTime()
            )
            : [];
        const latestAnswer = sortedAnswers[0] || { answer: "No answer available", created_on: item.question_created_on };

        const readableAnswer = formatAnswer(latestAnswer.answer);
        return {
            sno: index + 1,
            email: item.email || "Unknown email",
            topicName: item.topic_name || "Unknown topic",
            question: item.question || "No question",
            answer: readableAnswer,
            modelName: item.model_name || "Unknown model",
            createdOn: latestAnswer.created_on || item.question_created_on,
        };
    });
};

export const pythonStringToJSON = (uncleanedString: string): string => {
    return uncleanedString
        .replace(/(['"])?([a-zA-Z0-9_]+)(['"])?\s*:/g, '"$2":') // Ensure keys are double-quoted
        .replace(/:\s*'([^"]*?)'\s*(?=[,}])/g, ': "$1"')        // Quote values properly
        .replace(/\bNone\b/g, 'null')                           // Replace None with null
        .replace(/\bTrue\b/g, 'true')                           // Replace True with true
        .replace(/\bFalse\b/g, 'false')                         // Replace False with false
        .replace(/'(?![^"]*"\s*[},])/g, '"')                    // Replace single quotes with double quotes
        .replace(/(\w)"(\w)/g, "$1'$2");                        // Fix inner quotes (e.g., It"s -> It's)
};

const formatAnswer = (answer: string): string => {
    if (!answer || typeof answer !== 'string') return "No answer available";

    // Handle JSON string
    if (answer.startsWith('{') && answer.endsWith('}')) {
        try {
            const cleanedAnswer = pythonStringToJSON(answer);
            const parsed = JSON.parse(cleanedAnswer);

            if (parsed.data) {
                if (typeof parsed.data === 'string') {
                    return parsed.data.trim();
                } else if (Array.isArray(parsed.data)) {
                    if (parsed.type === 'chart') {
                        return parsed.data
                            .map((chart: any) => {
                                if (chart.title && chart.data) {
                                    const chartSummary = chart.data
                                        .map((entry: any) => {
                                            if (chart.chart_type === 'finance') {
                                                return `${entry.year}: Revenue $${entry.tr || 0}, Expenses $${entry.te || entry.fe || 0}`;
                                            } else if (chart.chart_type === 'functional') {
                                                return `${entry.year}: $${entry.amount || 0}`;
                                            }
                                            return `${entry.year}: $${entry.amount || 0}`;
                                        })
                                        .join('; ');
                                    return `${chart.title}: ${chartSummary}`;
                                }
                                return 'Invalid chart data';
                            })
                            .filter(Boolean)
                            .join('. ');
                    } else {
                        return parsed.data.join(', ');
                    }
                }
            }
            return "No data available in JSON";
        } catch (e) {
            console.error("Error parsing JSON answer:", e, "Raw answer:", answer);
            return answer.trim();
        }
    }

    // Handle HTML string
    if (answer.includes('<!DOCTYPE html>')) {
        const missionMatch = answer.match(/<div class="section-title">Mission<\/div>[\s\S]*?<p>(.*?)<\/p>/);
        if (missionMatch && missionMatch[1]) {
            return missionMatch[1];
        }
        const pTags = answer.match(/<p>.*?<\/p>/g)?.map((p) =>
            p.replace(/<p>(.*?)<\/p>/, '$1').replace(/<[^>]+>/g, '')
        );
        return pTags?.join(' ') || "HTML content (view details in console)";
    }

    // Plain text fallback
    return answer.trim();
};

export const getAvatarColor = (name: any) => {
    const firstLetter = name?.charAt(0).toUpperCase() || "U";
    const colors: any = {
        'A': '#54a0ff',
        'B': '#2ecc71',
        'C': '#e67e22',
        'D': '#9b59b6',
        'E': '#f1c40f',
        'F': '#1abc9c',
        'G': '#e84393',
        'H': '#fd79a8',
        'I': '#00cec9',
        'J': '#fdcb6e',
        'K': '#00b894',
        'L': '#ff7675',
        'M': '#6c5ce7',
        'N': '#74b9ff',
        'O': '#a29bfe',
        'P': '#e74c3c',
        'Q': '#81ecec',
        'R': '#fab1a0',
        'S': '#27ae60',
        'T': '#dfe6e9',
        'U': '#636e72',
        'V': '#badc58',
        'W': '#3498db',
        'X': '#8c7ae6',
        'Y': '#f0932b',
        'Z': '#ff9ff3'
    };
    return colors[firstLetter] || '#1890ff';
};

export const getAvatarText = (name: any) => {
    return name?.charAt(0).toUpperCase() || "U";
};

// Email validation function
export const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/* Calculates popover styles based on the current zoom level
Adjusts scaling and positioning to ensure consistent appearance at different zoom levels */
export const calculatePopoverStyles = (
    zoomLevel = 0.3,
    defaultZoom = 0.3,
    offsetTop = -40,
    offsetLeft = -20
) => {
    // Use provided zoomLevel or default to 0.3
    const currentZoom = zoomLevel || defaultZoom;
    const scaleFactor = defaultZoom / currentZoom;

    // Only apply scaling if zoom is below 1
    if (currentZoom < 1) {
        return {
            overlayInnerStyle: {
                transform: `scale(${scaleFactor})`,
                transformOrigin: 'top left'
            },
            overlayStyle: {
                // Adjust positioning to account for scaling
                marginTop: `${(scaleFactor - 1) * offsetTop}px`,
                marginLeft: `${(scaleFactor - 1) * offsetLeft}px`,
            }
        };
    }
    return {};
};